import React, { useState, useEffect, useCallback } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { Ticket, Calendar, MapPin, Clock, CheckCircle2, AlertTriangle, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { OrangeStreetTiers } from '@/components/OrangeStreetTiers';
import api from '@/services/api';
import { myEvents } from '@/lib/myEvents';
import { buildEventIndex, resolveSavedEvent } from '@/lib/eventAdapter';
import { useAuth } from '@/contexts/AuthContext';
import { usePageMeta } from '@/hooks/usePageMeta';

// A "sample ticket" is derived ENTIRELY from the user's own My Events record:
// it only exists when the user self-confirmed they are attending. There is no
// stored ticket, order, payment, QR code, or admission credential — it is a
// keepsake/confirmation card, clearly marked as not valid for entry.

// Stable, non-secret reference derived from the user-event record id so the
// same confirmed event always shows the same number (no randomness).
const refNumber = (rec) => {
  const base = String(rec?.id || rec?.event_id || '').replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
  return `SW-${(base.slice(0, 8) || '00000000').padEnd(8, '0')}`;
};

const displayName = (me, user) =>
  (me?.full_name || me?.name || user?.name || me?.username || me?.email || 'SoundWolves Member');

export const Tickets = () => {
  usePageMeta('My Tickets', 'Your confirmed SoundWolves event sample tickets.');
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const [me, setMe] = useState(null);
  const [items, setItems] = useState([]);
  const [eventsById, setEventsById] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [profile, mine, catalog] = await Promise.all([
        api.get('/users/me').catch(() => null),
        myEvents.list(),
        api.get('/events').catch(() => []),
      ]);
      setMe(profile);
      // Index by all identifier fields so confirmed events resolve regardless
      // of which identifier the saved record stored.
      setEventsById(buildEventIndex(catalog || []));
      // A sample ticket exists ONLY for self-confirmed attending records.
      setItems((mine || []).filter((i) => i.status === 'attending' && i.self_confirmed === true));
    } catch (e) {
      setError(e.message || 'Could not load your tickets');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) { setLoading(false); return; }
    load();
  }, [authLoading, isAuthenticated, load]);

  // Logged-out users never see Tickets — bounce to login.
  if (!authLoading && !isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return (
    <div className="min-h-screen pt-20 pb-24 md:pb-8" data-testid="tickets-page">
      <div className="container mx-auto px-4 max-w-3xl">
        <div className="mb-8">
          <h1 className="text-4xl sm:text-5xl font-display font-bold mb-3">
            My <span className="text-gradient-gold">Tickets</span>
          </h1>
          <p className="text-muted-foreground text-lg">
            Sample tickets for events you’ve confirmed you’re attending
          </p>
        </div>

        {/* ---- Sample tickets (from confirmed My Events) ---- */}
        {authLoading || loading ? (
          <div className="py-16 flex items-center justify-center" data-testid="tickets-loading">
            <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" />
          </div>
        ) : error ? (
          <Card className="p-8 sm:p-12 text-center border-destructive/30" data-testid="tickets-error">
            <AlertTriangle className="w-14 h-14 text-destructive mx-auto mb-4" />
            <h3 className="text-xl font-display font-bold mb-2">Couldn’t load your tickets</h3>
            <p className="text-muted-foreground mb-6">{error}</p>
            <Button variant="premium" onClick={load} data-testid="tickets-retry-btn">
              <RefreshCw className="w-4 h-4 mr-2" />Try Again
            </Button>
          </Card>
        ) : items.length === 0 ? (
          <Card className="p-8 sm:p-12 text-center" data-testid="tickets-empty">
            <Ticket className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-xl font-display font-bold mb-2">No sample tickets yet</h3>
            <p className="text-muted-foreground mb-6 max-w-md mx-auto">
              You don’t have any sample tickets yet. Confirm that you’re attending an event to see it here.
            </p>
            <Link to="/events">
              <Button variant="premium" data-testid="tickets-explore-btn">
                <Calendar className="w-4 h-4 mr-2" />Explore Events
              </Button>
            </Link>
          </Card>
        ) : (
          <div className="space-y-6" data-testid="tickets-list">
            {items.map((item) => {
              const ev = resolveSavedEvent(eventsById, item);
              const ref = refNumber(item);

              if (!ev) {
                return (
                  <Card key={item.id} className="overflow-hidden border-border/50 opacity-80" data-testid={`ticket-unavailable-${item.event_id}`}>
                    <CardContent className="p-6 flex items-start gap-4">
                      <AlertTriangle className="w-8 h-8 text-amber-500 flex-shrink-0" />
                      <div>
                        <h3 className="text-lg font-display font-bold mb-1">Event unavailable</h3>
                        <p className="text-sm text-muted-foreground">
                          This event is no longer available. Your confirmation reference is{' '}
                          <span className="font-mono">{ref}</span>.
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                );
              }

              return (
                <Card key={item.id} className="overflow-hidden border-primary/30 glass-card" data-testid={`ticket-${item.event_id}`}>
                  {ev.image && (
                    <div className="relative h-40 sm:h-48 overflow-hidden">
                      <img
                        src={ev.image}
                        alt={ev.title}
                        className="w-full h-full object-cover object-top"
                        onError={(e) => { e.currentTarget.style.display = 'none'; }}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-background via-background/70 to-transparent" />
                      <div className="absolute top-3 left-3">
                        <Badge className="bg-green-500/90 text-white backdrop-blur-sm">
                          <CheckCircle2 className="w-3 h-3 mr-1" />Attending
                        </Badge>
                      </div>
                      <div className="absolute bottom-4 left-5 right-5">
                        <h3 className="text-2xl font-display font-bold mb-1 leading-tight">{ev.title}</h3>
                        {ev.artist && <p className="text-sm text-primary font-medium">{ev.artist}</p>}
                      </div>
                    </div>
                  )}

                  <CardContent className="p-6 space-y-4">
                    <div>
                      <p className="text-xs uppercase tracking-wide text-muted-foreground">Ticket holder</p>
                      <p className="text-lg font-semibold" data-testid={`ticket-name-${item.event_id}`}>
                        {displayName(me, user)}
                      </p>
                    </div>

                    <Separator />

                    <div className="grid sm:grid-cols-2 gap-3 text-sm">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                        <span>{[ev.date, ev.time].filter(Boolean).join(' · ') || 'Date & time TBA'}</span>
                      </div>
                      {(ev.venue || ev.city) && (
                        <div className="flex items-center gap-2">
                          <MapPin className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                          <span>{[ev.venue, ev.city].filter(Boolean).join(', ')}</span>
                        </div>
                      )}
                    </div>

                    <Separator />

                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p className="text-xs uppercase tracking-wide text-muted-foreground">Reference</p>
                        <p className="font-mono text-base" data-testid={`ticket-ref-${item.event_id}`}>{ref}</p>
                      </div>
                      <Badge variant="secondary" className="text-green-500 border-green-500/30">
                        <CheckCircle2 className="w-3 h-3 mr-1" />Attending
                      </Badge>
                    </div>

                    <div className="mt-2 rounded-lg border border-dashed border-amber-500/40 bg-amber-500/5 p-4 text-center">
                      <p className="text-sm font-bold tracking-wide text-amber-500">SAMPLE TICKET ONLY</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        This is not a valid admission ticket and cannot be used for entry.
                      </p>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* ---- On sale now: Orange Street (real Eventbrite CTA, preserved) ---- */}
        <div className="mt-10">
          <h2 className="text-2xl font-display font-bold mb-4">On Sale Now</h2>
          <Card className="overflow-hidden border-border/50">
            <div className="relative h-48 overflow-hidden">
              <img
                src="/assets/events/poster-soundwolves.jpg"
                alt="Orange Street — Live in New Jersey poster"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-background via-background/80 to-transparent" />
              <div className="absolute bottom-4 left-6 right-6">
                <Badge className="bg-primary text-primary-foreground mb-2">Soundwolves Presents</Badge>
                <h3 className="text-2xl font-display font-bold mb-1">Orange Street — Live in New Jersey</h3>
                <p className="text-sm text-muted-foreground">Orange Street (Live) • DJ OM + DJ GHOST</p>
              </div>
            </div>
            <CardContent className="p-6 space-y-4">
              <div className="flex flex-wrap items-center gap-4 text-sm">
                <div className="flex items-center gap-2"><Calendar className="w-4 h-4 text-muted-foreground" /><span>Jul 10, 2026</span></div>
                <div className="flex items-center gap-2"><Clock className="w-4 h-4 text-muted-foreground" /><span>9:00 PM – 2:00 AM</span></div>
                <div className="flex items-center gap-2"><MapPin className="w-4 h-4 text-muted-foreground" /><span>XL Nightlife, Elizabeth NJ</span></div>
              </div>
              <Separator />
              <OrangeStreetTiers />
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Tickets;
